import React, { PropsWithChildren, useMemo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SecurityContextTemplate, PermissionsMap, useGlobalSecurityReactContext } from 'framework/authorization';
import useBillingService, { BillingScheduleResponse } from './billingService';
import { NEW_PROJECT_CONTEXT } from '../../NewProjectContext';
import { Loading } from 'framework/ui/mui-builder';
import { useProjectDetails } from '../../dashboard/hooks/useProjectDashboard';
import { ShowPageLayout } from 'framework/ui/layout';
import BillingBreadcrumbs from 'domain/projects/newProject/shared/breadcrumbs/BillingBreadcrumbs';
import { BillingType, ProjectSecurityContext } from 'domain/shared/newProject';

interface BillingRouteContextData {
  projectId: string;
  billingScheduleId: string;
  billingType?: BillingType;
}

const BillingRouteContextDataContext = React.createContext<Nullable<BillingRouteContextData>>(null);

export const useBillingRouteContext = () => {
  const context = React.useContext(BillingRouteContextDataContext);
  if (!context) {
    throw new Error('useBillingRouteContext must be used within BillingRouteContext');
  }
  return context;
};

export const BillingRouteContext = ({
  permissionToCheck,
  children,
}: PropsWithChildren<{ permissionToCheck: string | string[] }>) => {
  const params = useParams<Dictionary<string>>();
  const billingScheduleId = params.billingScheduleId!;
  const { billingScheduleSearch } = useBillingService();
  const globalContext = useGlobalSecurityReactContext() || {};
  const [billingSchedule, setBillingSchedule] = useState<BillingScheduleResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const projectId = billingSchedule?.projectId ? String(billingSchedule.projectId) : '';

  useEffect(() => {
    const fetchProjectId = async () => {
      try {
        const billingScheduleResponse = await billingScheduleSearch({
          pageSize: 1,
          filter: { id: { eq: billingScheduleId } },
        });
        const schedule = billingScheduleResponse?.result?.[0];
        setBillingSchedule(schedule);
      } catch (e) {
      } finally {
        setIsLoading(false);
      }
    };

    if (billingScheduleId) {
      fetchProjectId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingScheduleId]);

  const { project, isDataReloading } = useProjectDetails(projectId || '');

  const { contextValue, projectPermissionsInGlobalFormat, projectPermissions } = useMemo(() => {
    if (!project || !projectId) {
      return {
        contextValue: null,
        projectPermissionsInGlobalFormat: [],
        projectPermissions: undefined,
      };
    }

    const permissionsGlobalFormat =
      project?.accessInfo?.sections.flatMap(({ section, allowedActions }) =>
        allowedActions.map(permission => `GRAPHQL__${section}__${permission}`),
      ) || [];

    return {
      contextValue: {
        projectId: projectId,
        billingScheduleId,
        billingType: billingSchedule?.billingType,
      },
      projectPermissionsInGlobalFormat: permissionsGlobalFormat,
      projectPermissions: project?.accessInfo,
    };
  }, [project, projectId, billingScheduleId, billingSchedule?.billingType]);

  if (isLoading || isDataReloading) {
    return <Loading />;
  }

  if (!projectId || !project) {
    return null;
  }

  return (
    <SecurityContextTemplate
      id={projectId}
      getPermissionsFunc={() =>
        Promise.all([
          Promise.resolve(Object.keys(globalContext.permissions as unknown as PermissionsMap) || []),
          Promise.resolve(projectPermissionsInGlobalFormat || []),
        ]).then(permissions => {
          return permissions.reduce((flatten, arr) => [...flatten, ...arr]);
        })
      }
      permissionToCheck={permissionToCheck}
      contextName={NEW_PROJECT_CONTEXT}
    >
      <BillingRouteContextDataContext.Provider value={contextValue}>
        <ProjectSecurityContext permissions={projectPermissions}>
          <ShowPageLayout breadcrumbs={<BillingBreadcrumbs id={projectId} />}>{children}</ShowPageLayout>
        </ProjectSecurityContext>
      </BillingRouteContextDataContext.Provider>
    </SecurityContextTemplate>
  );
};
